import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/SocialForum.css";

import axiosInstance from "../utils/axiosInstance";
import { isLoggedIn } from "../utils/auth";

/* -------------------------------
   İsteğe bağlı özel model ağaçları
   - varsa öncelikli kullanılır
   - yoksa API models fallback olur
-------------------------------- */
const MODEL_TREE = {
  straumann: {
    STRAUMANN: [
      "iExcel",
      "X Line BLX",
      "X Line TLX",
      "C Line BLC",
      "C Line TLC",
      "Tissue level TLC",
      "Tissue level TLX",
      "Tissue level STANDART",
      "Tissue level STANDART PLUS",
      "Tissue level STANDART PLUS SHORT",
      "Tissue level STANDART PLUS NNC",
      "Tissue level CONIC",
      "Bone level BLC",
      "Bone level BLX",
      "Bone level TAPERED",
      "Zygomatic",
      "Mini",
      "PURE ceramic",
    ],
    NEODENT: ["Grand Morse", "ZI"],
    ANTHOGYR: ["Axiom", "Axiom bone level", "Axiom X3", "Axiom REG", "Axiom PX"],
  },

  "dentsply-sirona": {
    Ankylos: ["C/X"],
    "Astra Tech": ["OsseoSpeed EV", "Astra Tech EV", "PrimeTaper EV", "DS PrimeTaper", "EV"],
  },

  evoss: { Evoss: ["Evosoft", "Evoplus", "Evoshort", "Evoslim"] },

  "nobel-biocare": {
    "Nobel Biocare": [
      "N1 system",
      "Nobel Active",
      "Xeal",
      "TiUltra",
      "NobelParallel Conical",
      "Nobel Replace Conical",
      "Nobel Zygoma",
      "Nobel Pearl",
    ],
  },

  nucleoss: { Nucleoss: ["T6 Standart", "T6 Torq", "Slmex"] },
};

function buildModelOptions(brandSlug, apiModels = []) {
  const tree = MODEL_TREE[brandSlug];

  if (tree && Object.keys(tree).length > 0) {
    const out = ["Genel"];

    Object.entries(tree).forEach(([parent, children]) => {
      out.push(parent);
      (children || []).forEach((child) => out.push(`${parent} — ${child}`));
    });

    out.push("DİĞER");

    return Array.from(new Set(out.map((x) => String(x || "").trim()).filter(Boolean)));
  }

  const apiNames = (apiModels || [])
    .map((m) => String(m?.name || m?.code || "").trim())
    .filter(Boolean);

  return Array.from(new Set(["Genel", ...apiNames, "DİĞER"]));
}

function extractModelFromBody(text) {
  const s = String(text || "");
  const m = s.match(/^\[MODEL:\s*([^\]]+)\]\s*\n?/i);
  if (!m) return { model: null, cleaned: s };

  return {
    model: m[1].trim(),
    cleaned: s.replace(m[0], ""),
  };
}

// "Genel"/"DİĞER" backend'e gönderilen ve modelCode eşitlik kontrollerinde
// kullanılan GERÇEK değerler (bkz. handleCreateThread, buildModelOptions) —
// bunları değiştirmek gönderiminin/karşılaştırmaların bozulmasına yol açar.
// Bu fonksiyon SADECE ekranda gösterilen etiketi çevirir, value'ya dokunmaz.
function modelDisplayLabel(value, t) {
  if (value === "Genel") return t("socialBrandDetail.modelGeneral");
  if (value === "DİĞER") return t("socialBrandDetail.otherModel");
  return value;
}

function splitContactLines(note) {
  return String(note || "")
    .split(/\n+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export default function SocialBrandDetailPage() {
  const { t } = useTranslation();
  const { brandSlug } = useParams();

  const [brand, setBrand] = useState(null);
  const [brandLoading, setBrandLoading] = useState(true);

  const [modelFilter, setModelFilter] = useState("ALL");
  const [sort, setSort] = useState("latest");
  const [onlyMine, setOnlyMine] = useState(false);
  const [q, setQ] = useState("");

  const [modelCode, setModelCode] = useState("Genel");
  const [otherModelText, setOtherModelText] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [posting, setPosting] = useState(false);

  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);

  const [replyText, setReplyText] = useState({});
  const [replyImage, setReplyImage] = useState({});
  const [replyPosting, setReplyPosting] = useState({});

  useEffect(() => {
    let alive = true;

    async function fetchBrand() {
      if (!brandSlug) return;

      setBrandLoading(true);
      try {
        const { data } = await axiosInstance.get(`/api/social/brands/${brandSlug}/`);
        if (!alive) return;
        setBrand(data || null);
      } catch (err) {
        console.error("brand detail fetch error:", err);
        if (!alive) return;
        setBrand(null);
      } finally {
        if (alive) setBrandLoading(false);
      }
    }

    fetchBrand();

    return () => {
      alive = false;
    };
  }, [brandSlug]);

  const models = useMemo(() => {
    return buildModelOptions(brandSlug, brand?.models || []);
  }, [brandSlug, brand]);

  async function fetchThreads() {
    if (!brandSlug) return;

    setLoading(true);
    try {
      const params = { brand: brandSlug, sort };
      if (modelFilter !== "ALL") params.model = modelFilter;
      if (onlyMine) params.mine = "1";
      if (q.trim()) params.q = q.trim();

      const { data } = await axiosInstance.get("/api/social/threads/", { params });
      const arr = Array.isArray(data) ? data : data?.results || [];
      setThreads(arr);
    } catch (e) {
      console.error("threads fetch error:", e);
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!brandSlug) return;
    fetchThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandSlug, modelFilter, sort, onlyMine, q]);

  async function handleCreateThread(e) {
    e?.preventDefault?.();

    if (!isLoggedIn()) return alert(t("socialBrandDetail.alerts.loginToPost"));

    const s = subject.trim();
    if (!s) return alert(t("socialBrandDetail.alerts.subjectRequired"));
    if (s.length > 10) return alert(t("socialBrandDetail.alerts.subjectTooLong"));
    if (!modelCode) return alert(t("socialBrandDetail.alerts.selectModel"));

    const chosenLabel = modelCode === "DİĞER" ? (otherModelText || "").trim() : modelCode;

    if (modelCode === "DİĞER" && !chosenLabel) {
      return alert(t("socialBrandDetail.alerts.otherModelNameRequired"));
    }

    let finalBody = (body || "").trim();
    finalBody = `[MODEL: ${chosenLabel}]\n${finalBody}`;

    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("brand", brandSlug);
      fd.append("model", "DİĞER");
      fd.append("subject", s);
      fd.append("body", finalBody);
      if (imageFile) fd.append("image", imageFile);

      await axiosInstance.post("/api/social/threads/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSubject("");
      setBody("");
      setImageFile(null);
      setOtherModelText("");
      setModelCode("Genel");

      await fetchThreads();
    } catch (e2) {
      alert(e2?.response?.data?.detail || t("socialBrandDetail.alerts.postFailed"));
      console.error("create thread error:", e2);
    } finally {
      setPosting(false);
    }
  }

  async function submitReply(threadId) {
    if (!isLoggedIn()) return alert(t("socialBrandDetail.alerts.loginToReply"));

    const txt = (replyText[threadId] || "").trim();
    if (!txt) return alert(t("socialBrandDetail.alerts.replyEmpty"));

    setReplyPosting((s) => ({ ...s, [threadId]: true }));
    try {
      const fd = new FormData();
      fd.append("thread", String(threadId));
      fd.append("body", txt);
      if (replyImage[threadId]) fd.append("image", replyImage[threadId]);

      await axiosInstance.post("/api/social/posts/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setReplyText((s) => ({ ...s, [threadId]: "" }));
      setReplyImage((s) => ({ ...s, [threadId]: null }));
      await fetchThreads();
    } catch (e) {
      alert(e?.response?.data?.detail || t("socialBrandDetail.alerts.replyFailed"));
      console.error("reply error:", e);
    } finally {
      setReplyPosting((s) => ({ ...s, [threadId]: false }));
    }
  }

  async function toggleThreadLike(threadId) {
    if (!isLoggedIn()) return alert(t("socialBrandDetail.alerts.loginToLike"));
    try {
      const { data } = await axiosInstance.post("/api/social/thread-likes/toggle/", {
        thread_id: threadId,
      });
      setThreads((prev) =>
        prev.map((th) =>
          th.id === threadId ? { ...th, liked: !!data.liked, likes_count: data.likes_count } : th
        )
      );
    } catch (e) {
      if (e?.response?.status === 401) alert(t("socialBrandDetail.alerts.loginToLike"));
    }
  }

  async function togglePostLike(postId) {
    if (!isLoggedIn()) return alert(t("socialBrandDetail.alerts.loginToLike"));
    try {
      const { data } = await axiosInstance.post("/api/social/likes/toggle/", {
        post_id: postId,
      });
      setThreads((prev) =>
        prev.map((th) => ({
          ...th,
          posts: (th.posts || []).map((p) =>
            p.id === postId ? { ...p, liked: !!data.liked, likes_count: data.likes_count } : p
          ),
        }))
      );
    } catch (e) {
      if (e?.response?.status === 401) alert(t("socialBrandDetail.alerts.loginToLike"));
    }
  }

  const title = brand?.name || t("socialBrandDetail.brandFallback");
  const phones = brand?.contact?.phone ? [brand.contact.phone] : [];
  const emails = brand?.contact?.email ? [brand.contact.email] : [];
  const websites = brand?.contact?.website ? [brand.contact.website] : [];
  const addressLines = splitContactLines(brand?.contact?.note);

  if (brandLoading) {
    return (
      <div className="sf-page">
        <div className="sf-empty">{t("socialBrandDetail.brandLoading")}</div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="sf-page">
        <p>{t("socialBrandDetail.brandNotFound")}</p>
        <Link to="/social" className="sf-back">← {t("socialBrandDetail.back")}</Link>
      </div>
    );
  }

  return (
    <div className="sf-page">
      <div className="sf-topbar">
        <Link to="/social" className="sf-back">← {t("socialBrandDetail.backToSocial")}</Link>
      </div>

      <div className="sf-brand-head">
        <h1 className="sf-title">{title}</h1>

        <div className="sf-contact">
          {phones.length > 0 && (
            <div className="sf-contact-row">
              <span className="sf-pill">📞</span>
              <div>
                {phones.map((p) => (
                  <a key={p} className="sf-link" href={`tel:${p.replace(/\s+/g, "")}`}>
                    {p}
                  </a>
                ))}
              </div>
            </div>
          )}

          {emails.length > 0 && (
            <div className="sf-contact-row">
              <span className="sf-pill">✉️</span>
              <div>
                {emails.map((m) => (
                  <a key={m} className="sf-link" href={`mailto:${m}`}>
                    {m}
                  </a>
                ))}
              </div>
            </div>
          )}

          {websites.length > 0 && (
            <div className="sf-contact-row">
              <span className="sf-pill">🌐</span>
              <div>
                {websites.map((w) => (
                  <a key={w} className="sf-link" href={w} target="_blank" rel="noreferrer">
                    {w}
                  </a>
                ))}
              </div>
            </div>
          )}

          {addressLines.length > 0 && (
            <div className="sf-contact-row">
              <span className="sf-pill">📍</span>
              <div>
                {addressLines.map((a, i) => (
                  <div key={i} className="sf-text">
                    {a}
                  </div>
                ))}
              </div>
            </div>
          )}

          {phones.length === 0 &&
            emails.length === 0 &&
            websites.length === 0 &&
            addressLines.length === 0 && <div className="sf-text">{t("socialBrandDetail.noContactInfo")}</div>}
        </div>
      </div>

      <div className="sf-filters">
      <div className="sf-search">
        <span className="sf-search-ico">🔎</span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("socialBrandDetail.searchPlaceholder")} />
      </div>

      <select className="sf-select" value={modelFilter} onChange={(e) => setModelFilter(e.target.value)}>
        <option value="ALL">{t("socialBrandDetail.allModels")}</option>
        {models.map((m) => (
          <option key={m} value={m}>
            {modelDisplayLabel(m, t)}
          </option>
        ))}
      </select>

      <select className="sf-select" value={sort} onChange={(e) => setSort(e.target.value)}>
        <option value="latest">{t("socialBrandDetail.sortNewest")}</option>
        <option value="top">{t("socialBrandDetail.sortTop")}</option>
      </select>

      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
        {t("socialBrandDetail.onlyMine")}
      </label>
    </div>

      <form id="sf-create-form" className="sf-create" onSubmit={handleCreateThread}>
        <div className="sf-create-row">
          <div className="sf-create-col">
            <div className="sf-label">{t("socialBrandDetail.model")}</div>
            <select className="sf-select" value={modelCode} onChange={(e) => setModelCode(e.target.value)}>
              {models.map((m) => (
                <option key={m} value={m}>
                  {modelDisplayLabel(m, t)}
                </option>
              ))}
            </select>

            {modelCode === "DİĞER" && (
              <input
                className="sf-input"
                value={otherModelText}
                onChange={(e) => setOtherModelText(e.target.value)}
                placeholder={t("socialBrandDetail.otherModelPlaceholder")}
                maxLength={80}
                style={{ marginTop: 10 }}
              />
            )}
          </div>

          <div className="sf-create-col" style={{ flex: 1, minWidth: 220 }}>
            <div className="sf-label">{t("socialBrandDetail.subjectMax10")}</div>
            <input
              className="sf-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={10}
              placeholder={t("socialBrandDetail.subjectPlaceholder")}
            />
          </div>
        </div>

        <div className="sf-create-row">
          <div className="sf-create-col" style={{ flex: 1 }}>
            <div className="sf-label">{t("socialBrandDetail.content")}</div>
            <textarea
              className="sf-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("socialBrandDetail.contentPlaceholder")}
              rows={3}
            />
          </div>
        </div>

        <div className="sf-create-actions">
          <label className="sf-file">
            <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
            {t("socialBrandDetail.addImage")}
          </label>

          <button className="sf-btn" type="submit" disabled={posting}>
            {posting ? t("socialBrandDetail.sending") : t("socialBrandDetail.send")}
          </button>
        </div>
      </form>

      <div className="sf-topic-list">
        {loading ? (
          <div className="sf-empty">{t("socialBrandDetail.loading")}</div>
        ) : threads.length === 0 ? (
          <div className="sf-empty">{t("socialBrandDetail.noThreadsInFilter")}</div>
        ) : (
          threads.map((th) => {
            const { model: modelFromBody, cleaned } = extractModelFromBody(th.body);

            const modelName = modelFromBody || th.model_name || (th.model && th.model !== "DİĞER" ? th.model : "");

            const author = th.author_display || th.author_username || th.author || "";
            const when = th.created_display || th.created_at || "";
            const replies = th.replies_count ?? th.posts_count ?? (Array.isArray(th.posts) ? th.posts.length : 0);

            return (
              <article key={th.id} className="thread">
                <header className="thread-head">
                  <div className="thread-meta">
                    <span className="badge">{modelName || t("socialBrandDetail.otherModel")}</span>
                    <span className="subject" title={th.subject}>
                      {th.subject}
                    </span>
                    <span className="by">• {author}</span>
                  </div>

                  <div className="head-right" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <button
                      type="button"
                      className={`icon-btn thread-like ${th.liked ? "liked" : ""}`}
                      onClick={() => toggleThreadLike(th.id)}
                    >
                      ★ <span>{th.likes_count ?? 0}</span>
                    </button>

                    <span className="sf-replies">
                      {replies}
                      <div className="sf-replies-label">{t("socialBrandDetail.comment")}</div>
                    </span>

                    <time className="time">{when}</time>
                    <ThreadMenu thread={th} onChanged={fetchThreads} />
                  </div>
                </header>

                <div className="thread-body">
                  <span className="thread-label">{t("socialBrandDetail.question")}</span>
                  {th.image_url ? <img alt="" className="thread-img" src={th.image_url} /> : null}
                  <p>{(cleaned || "").trim() || "—"}</p>
                </div>

                <div className="posts">
                  {Array.isArray(th.posts) && th.posts.length > 0 && <div className="posts-label">{t("socialBrandDetail.answers")}</div>}

                  {(th.posts || []).map((p) => (
                    <div key={p.id} className={`post ${p.is_supplier_reply ? "supplier" : ""}`}>
                      <div className="post-head" style={{ display: "flex", justifyContent: "space-between" }}>
                        <div className="left">
                          {p.is_supplier_reply && (
                            <span className="pin" title={t("socialBrandDetail.supplierReplyTitle")}>
                              {t("socialBrandDetail.supplier")}
                            </span>
                          )}
                          <strong>{p.author_display || p.author_username || t("socialBrandDetail.userFallback")}</strong>
                          <span className="dot">•</span>
                          <time>{p.created_display || p.created_at || ""}</time>
                        </div>

                        <div className="right" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <button
                            type="button"
                            className={`icon-btn ${p.liked ? "liked" : ""}`}
                            onClick={() => togglePostLike(p.id)}
                          >
                            ♥ <span>{p.likes_count ?? 0}</span>
                          </button>
                          <PostMenu post={p} onChanged={fetchThreads} />
                        </div>
                      </div>

                      {p.image_url ? <img alt="" className="post-img" src={p.image_url} /> : null}
                      <p className="post-body">{p.body || ""}</p>
                    </div>
                  ))}
                </div>

                <div className="reply">
                  <textarea
                    rows={2}
                    placeholder={t("socialBrandDetail.replyPlaceholder")}
                    value={replyText[th.id] || ""}
                    onChange={(e) => setReplyText((s) => ({ ...s, [th.id]: e.target.value }))}
                  />

                  <div className="reply-actions">
                    <label className="btn ghost" style={{ position: "relative" }}>
                      {t("socialBrandDetail.image")}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => setReplyImage((s) => ({ ...s, [th.id]: e.target.files?.[0] || null }))}
                      />
                    </label>

                    {replyImage[th.id] ? <span className="file-chip">{replyImage[th.id].name}</span> : null}

                    <button className="btn" disabled={!!replyPosting[th.id]} onClick={() => submitReply(th.id)}>
                      {replyPosting[th.id] ? t("socialBrandDetail.replySending") : t("socialBrandDetail.reply")}
                    </button>
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}

function ThreadMenu({ thread, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const doDelete = async () => {
    setOpen(false);
    if (!window.confirm(t("socialBrandDetail.confirmDeleteThread"))) return;
    try {
      await axiosInstance.delete(`/api/social/threads/${thread.id}/`);
      onChanged?.();
    } catch (err) {
      alert(err?.response?.data?.detail || t("socialBrandDetail.deleteFailed"));
    }
  };

  return (
    <div className="kebab">
      <button type="button" className="icon-btn ghost" onClick={() => setOpen((o) => !o)} title={t("socialBrandDetail.options")}>
        ⋯
      </button>

      {open && (
        <div className="menu" role="menu">
          <button onClick={doDelete} className="danger" role="menuitem" type="button">
            {t("socialBrandDetail.delete")}
          </button>
        </div>
      )}
    </div>
  );
}

function PostMenu({ post, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const doDelete = async () => {
    setOpen(false);
    if (!window.confirm(t("socialBrandDetail.confirmDeletePost"))) return;
    try {
      await axiosInstance.delete(`/api/social/posts/${post.id}/`);
      onChanged?.();
    } catch (err) {
      alert(err?.response?.data?.detail || t("socialBrandDetail.deleteFailed"));
    }
  };

  return (
    <div className="kebab">
      <button type="button" className="icon-btn ghost" onClick={() => setOpen((o) => !o)} title={t("socialBrandDetail.options")}>
        ⋯
      </button>

      {open && (
        <div className="menu" role="menu">
          <button onClick={doDelete} className="danger" role="menuitem" type="button">
            {t("socialBrandDetail.delete")}
          </button>
        </div>
      )}
    </div>
  );
}